//videoPreference.ts — camera, rotation and mirror choices, persisted in localStorage

import { isModeChoice, type ModeChoice } from '../../shared/realtimeModel'

export type QuarterTurns = 0 | 1 | 2 | 3
export type UpscaleMode = 'off' | 'shader' | 'neural' | 'sidecar'

const UPSCALE_MODES: readonly UpscaleMode[] = ['off', 'shader', 'neural', 'sidecar']

export interface VideoPreference {
	readonly deviceId: string | null
	//Rotates the frames actually sent to Lucy, which costs a canvas pass. Defaults to 0:
	//rotating landscape to portrait and then cropping back crops and upscales, which
	//visibly wrecks quality. Only set this if the camera is physically mounted rotated.
	readonly captureRotation: QuarterTurns
	readonly mirror: boolean
	//Which upscale engine presents the live Lucy frames. Shader is the safe default;
	//neural needs WebGPU; sidecar needs the local helper and a localhost origin.
	readonly upscaleMode: UpscaleMode
	//Remembers the engine while the quick toggle has the upscaler off.
	readonly lastUpscaleEngine: Exclude<UpscaleMode, 'off'>
	//Hides the top-left status readout. The elapsed time and running cost are useful while
	//operating the mirror and look like a dev tool on camera, so they come off for a recording.
	readonly showStatus: boolean
	//Hides the garment dock at the bottom for a full, unobstructed view of the try-on.
	//Selection still works from the settings panel's start action.
	readonly showDock: boolean
	//Follow the admin's catalogue, or a local scene-model override (the field name predates
	//modes). Token-pinned, so a change applies on the next start.
	readonly model: ModeChoice
	//Free-text prompt for the non-try-on models, which transform the whole scene.
	readonly scenePrompt: string
	//Realtime engine/resolution/fps HUD for A/B testing the upscale paths.
	readonly showUpscaleStats: boolean
}

const STORAGE_KEY = 'mirrorTryOn.video.v1'

export const DEFAULT_PREFERENCE: VideoPreference = {
	deviceId: null,
	captureRotation: 0,
	//A mirror should behave like a mirror.
	mirror: true,
	upscaleMode: 'shader',
	lastUpscaleEngine: 'shader',
	showStatus: true,
	showDock: true,
	showUpscaleStats: false,
	model: 'auto',
	scenePrompt: '',
}

export function loadPreference(): VideoPreference {
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		if (!raw) return DEFAULT_PREFERENCE
		return sanitise({ ...DEFAULT_PREFERENCE, ...(JSON.parse(raw) as Partial<VideoPreference>) })
	} catch {
		//A corrupt or unreadable store must not stop the mirror from opening.
		return DEFAULT_PREFERENCE
	}
}

export function savePreference(preference: VideoPreference): VideoPreference {
	const clean = sanitise(preference)
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(clean))
	} catch {
		//Private-mode Safari throws on write. Losing persistence is acceptable; crashing is not.
	}
	return clean
}

//Anything read back from storage is untrusted input.
function sanitise(preference: VideoPreference): VideoPreference {
	return {
		deviceId: typeof preference.deviceId === 'string' && preference.deviceId ? preference.deviceId : null,
		captureRotation: asQuarterTurns(preference.captureRotation),
		mirror: Boolean(preference.mirror),
		upscaleMode: asUpscaleMode(preference),
		lastUpscaleEngine: asLastEngine(preference),
		showStatus: preference.showStatus !== false,
		showDock: preference.showDock !== false,
		showUpscaleStats: preference.showUpscaleStats === true,
		//Older builds stored 'lucy-vton-3.5' or 'character' here; the admin decides those now.
		model: isModeChoice(preference.model) ? preference.model : 'auto',
		scenePrompt: typeof preference.scenePrompt === 'string' ? preference.scenePrompt : '',
	}
}

function asUpscaleMode(preference: VideoPreference): UpscaleMode {
	const legacy = preference as VideoPreference & { sharpUpscale?: boolean }
	if (UPSCALE_MODES.includes(legacy.upscaleMode)) return legacy.upscaleMode
	//Pre-mode builds stored a boolean; false meant the upscaler was switched off.
	return legacy.sharpUpscale === false ? 'off' : 'shader'
}

function asLastEngine(preference: VideoPreference): Exclude<UpscaleMode, 'off'> {
	const mode = asUpscaleMode(preference)
	if (mode !== 'off') return mode
	const stored = preference.lastUpscaleEngine
	return stored === 'neural' || stored === 'sidecar' ? stored : 'shader'
}

function asQuarterTurns(value: unknown): QuarterTurns {
	const turns = Number(value)
	return turns === 1 || turns === 2 || turns === 3 ? turns : 0
}

export const rotationDegrees = (turns: QuarterTurns): number => turns * 90
